import { AgencyId } from "@inrev/common";
import { useAdminFetchAgentPreviewsForAgency } from "@portal/api/agency/admin";
import { FollowerAvatarGroup } from "@portal/components/features/followers/FollowerAvatarGroup";
import { cn } from "@portal/components/lib/utils";
import { Text } from "@portal/components/primitives";
import {
	FollowerControlsProps,
	ManageFollowersModal,
} from "@portal/components/ui/FollowerControls";
import { Followers } from "@portal/components/ui/Followers";
import { trackingAttrs } from "@portal/components/utils";
import { useState } from "react";

export type AdminFollowerControlsProps = Omit<
	FollowerControlsProps,
	"notFollowingLabel" | "useFetchAgentPreviews" | "trackingId"
> & {
	agencyId: AgencyId;
	/**
	 * A prefix, not an element id -- unlike the agent FollowerControls there is no
	 * follow button here, so only the `-manage` affordance is ever stamped.
	 */
	trackingPrefix?: string;
	/** Root overrides — pass `pt-0` where the parent owns the vertical rhythm. */
	className?: string;
	/**
	 * `count` is the legacy pill. `avatars` renders an `AvatarGroup` plus a dashed add button.
	 * @default "count"
	 */
	appearance?: "count" | "avatars";
};

export const AdminFollowerControls = ({
	className,
	appearance = "count",
	...props
}: AdminFollowerControlsProps) => {
	const [manageFollowersModalOpen, setManageFollowersModalOpen] = useState<boolean>(false);
	const openManageFollowers = () => setManageFollowersModalOpen(true);

	return (
		<div className={cn("flex items-center space-x-[8px] pt-[6px]", className)}>
			<Text type="caption" color="secondary" className="font-[550]">
				Agent Followers
			</Text>
			{appearance === "avatars" ? (
				<div
					{...trackingAttrs(props.trackingPrefix ? `${props.trackingPrefix}-manage` : undefined)}
				>
					<FollowerAvatarGroup
						followers={props.followers ?? []}
						onAdd={openManageFollowers}
						addLabel="Add follower"
					/>
				</div>
			) : (
				!!props.followers && (
					<div
						{...trackingAttrs(props.trackingPrefix ? `${props.trackingPrefix}-manage` : undefined)}
					>
						<Followers followers={props.followers} onClick={openManageFollowers} />
					</div>
				)
			)}
			{manageFollowersModalOpen && (
				<ManageFollowersModal
					{...props}
					onClose={() => setManageFollowersModalOpen(false)}
					useFetchAgentPreviews={() => useAdminFetchAgentPreviewsForAgency(props.agencyId, true)}
				/>
			)}
		</div>
	);
};
